import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import "./publishPreview.css";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

const normalizeError = (err: any): string => {
  if (!err) return "Something went wrong";
  if (typeof err === "string") return err;
  if (Array.isArray(err)) return err.map(normalizeError).join(", ");
  if (typeof err === "object") {
    if (err.detail) return normalizeError(err.detail);
    if (err.msg) return err.msg;
    return "Unexpected server error";
  }
  return "Unexpected error";
};

export default function PublishPreviewPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as any;

  const [title, setTitle] = useState(state?.title || "");
  const [caption, setCaption] = useState(state?.caption || "");
  const [description, setDescription] = useState(state?.description || "");
  const [tags, setTags] = useState<string[]>(state?.tags || []);
  const [image, setImage] = useState<string>(
    typeof state?.image === "string" ? state.image : ""
  );
  const [draftId] = useState<any>(state?.draftId || 0);


  const [visibility, setVisibility] = useState<"public" | "friends-only" | "private">("public");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!state) {
      navigate("/Home");
    }
  }, [state, navigate]);

  /* -----------------------------
     ACTIONS
  ----------------------------- */

  const postToGelwel = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!image) {
        setError("Image missing. Please go back and select an image.");
        return;
      }

      // if (!draftId) {
      //   setError("Draft ID missing. Please go back and create draft again.");
      //   return;
      // }

      const blob = await fetch(image).then((r) => r.blob());
      const file = new File([blob], "post.jpg", { type: blob.type });

      const form = new FormData();

      const finalCaption = `${title}\n\n${description}\n\n${caption}`;

      form.append("caption", finalCaption);
      form.append("tags", tags.join(","));
      form.append("visibility", visibility);
      form.append("media", file);
      form.append("draft_id", draftId);

      await axios.post(`${API}/posts`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      navigate("/my-posts");
    } catch (e: any) {
      const apiError = e.response?.data || e.message;
      setError(normalizeError(apiError));
    } finally {
      setLoading(false);
    }
  };

  const saveForLater = async () => {
    try {
      setLoading(true);
      setError(null);

      const combined = `${title}\n\n${description}\n\n${caption}`;

      await axios.post(
        `${API}/drafts`,
        { content: combined, platform: "instagram" },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Saved for later");
    } catch (e: any) {
      const apiError = e.response?.data || e.message;
      setError(normalizeError(apiError));
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------
     SHARE
  ----------------------------- */

  const shareTwitter = () => {
    const text = encodeURIComponent(`${title}\n${caption}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };

  const shareFacebook = () => {
    const url = encodeURIComponent(image);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
  };

  const shareInstagram = () => {
    alert("Instagram does not support direct web sharing. Please download & upload in app.");
  };

  /* -----------------------------
     RENDER
  ----------------------------- */

  return (
    <motion.div
      className="publish-preview-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="preview-container">

        {/* IMAGE */}
        <motion.div
          className="preview-image-wrapper"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <img src={image} alt="preview" className="preview-image-full" />
        </motion.div>

        {/* CONTENT */}
        <motion.div
          className="preview-content"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >

          <input
            className="preview-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter title..."
          />

          <textarea
            className="preview-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Write description..."
          />

          <textarea
            className="preview-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write caption..."
          />

          {/* VISIBILITY */}
          <div className="visibility-row">
            <span>Visibility</span>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as any)}
            >
              <option value="public">Public</option>
              <option value="friends-only">Friends Only</option>
              <option value="private">Only Me</option>
            </select>
          </div>

          {/* TAGS */}
          <div className="tag-row">
            {tags.map((tag, i) => (
              <span key={i} className="tag-chip">
                #{tag}
              </span>
            ))}
          </div>

        </motion.div>

        {/* ACTION BAR */}
        <motion.div
          className="action-bar"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button onClick={postToGelwel} disabled={loading}>
            {loading ? "Posting..." : "Post on Gelwel"}
          </button>

          <button className="secondary" onClick={saveForLater} disabled={loading}>
            Save for Later
          </button>
        </motion.div>

        {/* SHARE BAR */}
        <motion.div
          className="share-bar"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <button onClick={shareInstagram}>Instagram</button>
          <button onClick={shareTwitter}>Twitter</button>
          <button onClick={shareFacebook}>Facebook</button>
        </motion.div>

        {error && <div className="error-box">{error}</div>}
      </div>
    </motion.div>
  );
}
